import { Injectable, NgZone, ApplicationRef } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";

/**
 * Global service that ensures Angular change detection runs
 * whenever the NgRx store state changes.
 *
 * This is a centralized fix for the issue where UI doesn't update
 * after API responses in Angular 21 with zone.js 0.15.0.
 *
 * The problem: Store subscriptions may fire outside Angular's zone,
 * so Angular doesn't know to run change detection.
 *
 * The solution: Subscribe to all store changes and ensure we re-enter
 * Angular's zone, which automatically triggers change detection.
 *
 * Usage: Initialize in AppComponent.ngOnInit()
 */
@Injectable({ providedIn: "root" })
export class StoreChangeDetectionService {
  private subscription: Subscription | null = null;
  private initialized = false;
  private rafId: number | null = null;

  constructor(
    private store: Store<any>,
    private ngZone: NgZone,
    private appRef: ApplicationRef
  ) {}

  /**
   * Initialize the global change detection listener.
   * Call this once in AppComponent.ngOnInit()
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    // Subscribe to all store changes
    this.subscription = this.store.subscribe(() => {
      this.scheduleChangeDetection();
    });
  }

  /**
   * Schedule change detection using requestAnimationFrame.
   * This batches multiple rapid store changes into a single CD cycle.
   */
  private scheduleChangeDetection(): void {
    // Skip if already scheduled
    if (this.rafId !== null) {
      return;
    }

    // Guard for non-browser environments (SSR, unit tests without jsdom)
    if (typeof requestAnimationFrame === 'undefined') {
      return;
    }

    // Use requestAnimationFrame to batch changes and avoid recursive ticks
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;

      // Only tick if we're outside a current change detection cycle
      // Check if we're inside NgZone - if not, enter it
      if (!NgZone.isInAngularZone()) {
        this.ngZone.run(() => {
          // Just entering the zone may not be enough in Angular 21
          // So we explicitly tick, but only when truly outside zone
          this.safeTickAll();
        });
      } else {
        // Already in zone, just tick
        this.safeTickAll();
      }
    });
  }

  /**
   * Safely tick all views, catching and ignoring NG0101 errors
   */
  private safeTickAll(): void {
    try {
      // Use getAllAngularTestabilities approach for more reliable detection
      const views = (this.appRef as any)._views;
      if (views && views.length > 0) {
        views.forEach((view: any) => {
          if (view.rootNodes && view.rootNodes.length > 0) {
            try {
              view.detectChanges();
            } catch (e) {
              // Ignore individual view errors
            }
          }
        });
      }
      // Also tick the application ref itself
      this.appRef.tick();
    } catch (e) {
      // Ignore NG0101 recursive tick errors
      if (!(e instanceof Error) || !e.message?.includes('NG0101')) {
        console.warn('StoreChangeDetectionService tick error:', e);
      }
    }
  }

  /**
   * Cleanup subscription (call in AppComponent.ngOnDestroy if needed)
   */
  destroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this.initialized = false;
  }
}
